import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { hasPermission } from "../lib/permissions";
import { useT } from "../i18n/I18nProvider";
import { CopyButton } from "../components/shared/CopyButton";
import { confirmDialog } from "../components/shared/modals";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? "";
const BACKEND_BASE = BACKEND_URL.replace(/\/$/, "");
// Per-org webhook URLs — the org_id is part of the path so the link
// to the organisation is explicit and the route handler can scope
// the secret/signature check to that exact org instead of falling
// back to a global lookup.
function inboundWebhookUrl(orgId: string): string {
  return `${BACKEND_BASE}/api/email/inbound/${encodeURIComponent(orgId)}`;
}
function resendWebhookUrl(orgId: string): string {
  return `${BACKEND_BASE}/api/email/resend-webhook/${encodeURIComponent(orgId)}`;
}

const INBOUND_PAYLOAD_EXAMPLE = JSON.stringify(
  {
    from: '"Lien De Smet" <lien@example.com>',
    to: ["support@example.com"],
    subject: "Re: [#1042] Kan niet inloggen",
    body: "Bedankt voor je antwoord, het werkt nu.",
    messageId: "<CABcd1234@mail.example.com>",
    inReplyTo: "<noreply.1042@support.example.be>",
  },
  null,
  2,
);

type OutboundProvider = "smtp" | "resend";
type InboundMethod = "webhook" | "imap" | "both";
type Tab = "outbound" | "inbound" | "webhooks" | "templates";
type Status = "ready" | "incomplete" | "empty";

interface RedactedSettings {
  orgId: string;
  outboundProvider: OutboundProvider;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  smtpPassSet: boolean;
  smtpFrom: string | null;
  resendApiKeySet: boolean;
  resendWebhookSecretSet: boolean;
  supportEmail: string | null;
  inboundMethod: InboundMethod;
  inboundEmailSecretSet: boolean;
  imapHost: string | null;
  imapPort: number | null;
  imapUser: string | null;
  imapPassSet: boolean;
  imapUseTls: boolean;
  imapFolder: string;
  imapPollSeconds: number;
  updatedAt?: string;
}

const SECRET_PLACEHOLDER = "••••••••";

export default function Settings() {
  const api = useApi();
  const t = useT();
  const { user } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  // Settings is admin-only. settings.write is unique to the admin role,
  // so it gates both reading and writing here. Non-admins are bounced
  // to /tickets below.
  const canEdit = hasPermission(roles, "settings.write");
  const canRead = canEdit;

  const [tab, setTab] = useState<Tab>("outbound");
  const [current, setCurrent] = useState<RedactedSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPayload, setShowPayload] = useState(false);

  // Outbound
  const [outboundProvider, setOutboundProvider] = useState<OutboundProvider>("smtp");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState<string>("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [resendApiKey, setResendApiKey] = useState("");
  const [resendWebhookSecret, setResendWebhookSecret] = useState("");
  const [supportEmail, setSupportEmail] = useState("");

  // Inbound
  const [inboundMethod, setInboundMethod] = useState<InboundMethod>("webhook");
  const [inboundSecret, setInboundSecret] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState<string>("993");
  const [imapUser, setImapUser] = useState("");
  const [imapPass, setImapPass] = useState("");
  const [imapUseTls, setImapUseTls] = useState(true);
  const [imapFolder, setImapFolder] = useState("INBOX");
  const [imapPollSeconds, setImapPollSeconds] = useState<string>("120");

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [imapTesting, setImapTesting] = useState(false);
  const [imapResult, setImapResult] = useState<{ ok: boolean; processed: number; lastUid: number; durationMs: number; error?: string } | null>(null);

  const load = async () => {
    const res = await api.get<RedactedSettings>("/api/settings");
    setCurrent(res);
    setOutboundProvider(res.outboundProvider);
    setSmtpHost(res.smtpHost ?? "");
    setSmtpPort(String(res.smtpPort ?? 587));
    setSmtpUser(res.smtpUser ?? "");
    setSmtpFrom(res.smtpFrom ?? "");
    setSupportEmail(res.supportEmail ?? "");
    setSmtpPass("");
    setResendApiKey("");
    setResendWebhookSecret("");

    setInboundMethod(res.inboundMethod);
    setInboundSecret("");
    setImapHost(res.imapHost ?? "");
    setImapPort(String(res.imapPort ?? 993));
    setImapUser(res.imapUser ?? "");
    setImapPass("");
    setImapUseTls(res.imapUseTls);
    setImapFolder(res.imapFolder ?? "INBOX");
    setImapPollSeconds(String(res.imapPollSeconds ?? 120));
  };

  useEffect(() => {
    if (!canRead) return;
    load().catch((err) => setLoadError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  // Compute readiness from the persisted settings (not the dirty form state)
  // so the badges reflect what the backend will actually use.
  const outboundStatus: Status = useMemo(() => {
    if (!current) return "empty";
    if (current.outboundProvider === "smtp") {
      const fields = [current.smtpHost, current.smtpPort, current.smtpUser, current.smtpFrom];
      const filled = fields.filter(Boolean).length + (current.smtpPassSet ? 1 : 0);
      if (filled === 0) return "empty";
      if (filled < 5) return "incomplete";
      return "ready";
    }
    const filled = (current.resendApiKeySet ? 1 : 0) + (current.smtpFrom ? 1 : 0);
    if (filled === 0) return "empty";
    if (filled < 2) return "incomplete";
    return "ready";
  }, [current]);

  const inboundStatus: Status = useMemo(() => {
    if (!current) return "empty";
    const wantsWebhook = current.inboundMethod === "webhook" || current.inboundMethod === "both";
    const wantsImap = current.inboundMethod === "imap" || current.inboundMethod === "both";
    const webhookOk = !wantsWebhook || current.inboundEmailSecretSet;
    const imapFields = [current.imapHost, current.imapPort, current.imapUser];
    const imapFilled = imapFields.filter(Boolean).length + (current.imapPassSet ? 1 : 0);
    const imapOk = !wantsImap || imapFilled === 4;
    if (wantsWebhook && !webhookOk && !current.imapPassSet && imapFilled === 0) return "empty";
    if (webhookOk && imapOk) return "ready";
    return "incomplete";
  }, [current]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setMessage(null);
    try {
      const patch: Record<string, unknown> = {
        outboundProvider,
        smtpFrom: smtpFrom || null,
        supportEmail: supportEmail || null,
        inboundMethod,
      };

      if (outboundProvider === "smtp") {
        patch.smtpHost = smtpHost || null;
        patch.smtpPort = smtpPort ? Number(smtpPort) : null;
        patch.smtpUser = smtpUser || null;
        if (smtpPass) patch.smtpPass = smtpPass;
      }
      if (outboundProvider === "resend" && resendApiKey) {
        patch.resendApiKey = resendApiKey;
      }
      if (resendWebhookSecret) patch.resendWebhookSecret = resendWebhookSecret;

      if (inboundMethod === "webhook" || inboundMethod === "both") {
        if (inboundSecret) patch.inboundEmailSecret = inboundSecret;
      }
      if (inboundMethod === "imap" || inboundMethod === "both") {
        patch.imapHost = imapHost || null;
        patch.imapPort = imapPort ? Number(imapPort) : null;
        patch.imapUser = imapUser || null;
        if (imapPass) patch.imapPass = imapPass;
        patch.imapUseTls = imapUseTls;
        patch.imapFolder = imapFolder || "INBOX";
        patch.imapPollSeconds = imapPollSeconds ? Number(imapPollSeconds) : 120;
      }

      const res = await api.patch<RedactedSettings>("/api/settings", patch);
      setCurrent(res);
      setSmtpPass("");
      setResendApiKey("");
      setResendWebhookSecret("");
      setInboundSecret("");
      setImapPass("");
      setMessage({ kind: "ok", text: t("settings.saved") });
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // Non-admin lands here only by typing the URL or following a stale
  // bookmark — bounce them to the ticket list instead of showing an
  // empty page.
  if (!canRead) {
    return <Navigate to="/tickets" replace />;
  }

  if (loadError) {
    return (
      <>
        <h2>{t("settings.title")}</h2>
        <div className="card" style={{ color: "var(--danger)" }}>
          <p style={{ marginTop: 0 }}>{t("settings.loadFailed")}</p>
          <p style={{ color: "var(--text-muted)" }}>{loadError}</p>
          <button onClick={() => { setLoadError(null); load().catch((err) => setLoadError((err as Error).message)); }}>
            {t("common.tryAgain")}
          </button>
        </div>
      </>
    );
  }

  if (!current) return <p>{t("common.loading")}</p>;

  const providerLabel = outboundProvider === "smtp"
    ? t("settings.outbound.providerSmtp")
    : t("settings.outbound.providerResend");

  const inboundMethodLabel = inboundMethod === "webhook"
    ? "Webhook"
    : inboundMethod === "imap"
    ? "IMAP"
    : "Webhook + IMAP";

  return (
    <>
      <h2>{t("settings.title")}</h2>
      <p style={{ color: "var(--text-muted)" }}>{t("settings.intro")}</p>

      {/* Status summary — at-a-glance overview of what's wired up. */}
      <div className="settings-summary">
        <SummaryItem
          label={t("settings.summary.outbound")}
          value={providerLabel}
          status={outboundStatus}
        />
        <SummaryItem
          label={t("settings.summary.inbound")}
          value={inboundMethodLabel}
          status={inboundStatus}
        />
      </div>

      {/* Tabs split the long form into three focused screens. */}
      <div className="settings-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "outbound"}
          className={tab === "outbound" ? "active" : ""}
          onClick={() => setTab("outbound")}
        >
          {t("settings.tabs.outbound")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "inbound"}
          className={tab === "inbound" ? "active" : ""}
          onClick={() => setTab("inbound")}
        >
          {t("settings.tabs.inbound")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "webhooks"}
          className={tab === "webhooks" ? "active" : ""}
          onClick={() => setTab("webhooks")}
        >
          {t("settings.tabs.webhooks")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "templates"}
          className={tab === "templates" ? "active" : ""}
          onClick={() => setTab("templates")}
        >
          {t("settings.tabs.templates")}
        </button>
      </div>

      <form onSubmit={save} className="settings-form">
        {tab === "outbound" && (
          <div className="card">
            <div className="field">
              <label>{t("settings.outbound.provider")}</label>
              <div className="segmented">
                <button
                  type="button"
                  className={outboundProvider === "smtp" ? "active" : ""}
                  onClick={() => canEdit && setOutboundProvider("smtp")}
                  disabled={!canEdit}
                >
                  {t("settings.outbound.providerSmtp")}
                </button>
                <button
                  type="button"
                  className={outboundProvider === "resend" ? "active" : ""}
                  onClick={() => canEdit && setOutboundProvider("resend")}
                  disabled={!canEdit}
                >
                  {t("settings.outbound.providerResend")}
                </button>
              </div>
            </div>

            <div className="form-grid form-grid-2">
              <div className="field">
                <label>{t("settings.outbound.from")}</label>
                <input value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} type="email" placeholder="noreply@example.com" disabled={!canEdit} />
                <small style={{ color: "var(--text-muted)" }}>{t("settings.outbound.fromHelp")}</small>
              </div>
              <div className="field">
                <label>{t("settings.outbound.replyTo")}</label>
                <input value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} type="email" placeholder="support@example.com" disabled={!canEdit} />
              </div>
            </div>

            {outboundProvider === "smtp" && (
              <fieldset className="settings-fieldset">
                <legend>{t("settings.section.smtpCreds")}</legend>
                <div className="form-grid form-grid-3-1">
                  <div className="field">
                    <label>{t("settings.outbound.smtpHost")}</label>
                    <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" disabled={!canEdit} />
                  </div>
                  <div className="field">
                    <label>{t("settings.outbound.smtpPort")}</label>
                    <input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} type="number" min={1} max={65535} disabled={!canEdit} />
                  </div>
                </div>
                <div className="form-grid form-grid-2">
                  <div className="field">
                    <label>{t("settings.outbound.smtpUser")}</label>
                    <input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} disabled={!canEdit} />
                  </div>
                  <div className="field">
                    <label>{t("settings.outbound.smtpPass")}</label>
                    <input
                      value={smtpPass}
                      onChange={(e) => setSmtpPass(e.target.value)}
                      type="password"
                      placeholder={current.smtpPassSet ? SECRET_PLACEHOLDER : t("settings.outbound.smtpPassPlaceholder")}
                      disabled={!canEdit}
                    />
                    <small style={{ color: "var(--text-muted)" }}>
                      {current.smtpPassSet ? t("settings.outbound.secretSet") : t("settings.outbound.secretUnset")}
                    </small>
                  </div>
                </div>
              </fieldset>
            )}

            {outboundProvider === "resend" && (
              <div className="field">
                <label>{t("settings.outbound.resendKey")}</label>
                <input
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  type="password"
                  placeholder={current.resendApiKeySet ? SECRET_PLACEHOLDER : "re_xxxxxxxx"}
                  disabled={!canEdit}
                />
                <small style={{ color: "var(--text-muted)" }}>
                  {current.resendApiKeySet ? t("settings.outbound.resendKeySet") : t("settings.outbound.resendKeyUnset")}
                </small>
              </div>
            )}
          </div>
        )}

        {tab === "inbound" && (
          <div className="card">
            <div className="field">
              <label>{t("settings.inbound.method")}</label>
              <div className="segmented">
                <button
                  type="button"
                  className={inboundMethod === "webhook" ? "active" : ""}
                  onClick={() => canEdit && setInboundMethod("webhook")}
                  disabled={!canEdit}
                >
                  Webhook
                </button>
                <button
                  type="button"
                  className={inboundMethod === "imap" ? "active" : ""}
                  onClick={() => canEdit && setInboundMethod("imap")}
                  disabled={!canEdit}
                >
                  IMAP
                </button>
                <button
                  type="button"
                  className={inboundMethod === "both" ? "active" : ""}
                  onClick={() => canEdit && setInboundMethod("both")}
                  disabled={!canEdit}
                >
                  {t("settings.inbound.methodBoth")}
                </button>
              </div>
            </div>

            {(inboundMethod === "webhook" || inboundMethod === "both") && (
              <fieldset className="settings-fieldset">
                <legend>{t("settings.section.inboundWebhookHeading")}</legend>
                <div className="field">
                  <label>{t("settings.inbound.webhookSecret")}</label>
                  <input
                    value={inboundSecret}
                    onChange={(e) => setInboundSecret(e.target.value)}
                    type="password"
                    placeholder={current.inboundEmailSecretSet ? SECRET_PLACEHOLDER : t("settings.inbound.webhookSecretPlaceholder")}
                    disabled={!canEdit}
                    minLength={8}
                  />
                  <small style={{ color: "var(--text-muted)" }}>
                    {current.inboundEmailSecretSet ? t("settings.inbound.webhookSecretSet") : t("settings.inbound.webhookSecretUnset")}
                  </small>
                </div>
                <p style={{ color: "var(--text-muted)", marginTop: 0, fontSize: 12 }}>
                  {t("settings.section.inboundWebhookHelp")} {t("settings.inbound.webhookExplain")}{" "}
                  <Link to="#webhooks" onClick={(e) => { e.preventDefault(); setTab("webhooks"); }}>
                    {t("settings.inbound.webhookUrl")} →
                  </Link>
                </p>
              </fieldset>
            )}

            {(inboundMethod === "imap" || inboundMethod === "both") && (
              <>
                <fieldset className="settings-fieldset">
                  <legend>{t("settings.section.imapServer")}</legend>
                  <div className="form-grid form-grid-3-1">
                    <div className="field">
                      <label>{t("settings.imap.host")}</label>
                      <input value={imapHost} onChange={(e) => setImapHost(e.target.value)} placeholder="imap.example.com" disabled={!canEdit} />
                    </div>
                    <div className="field">
                      <label>{t("settings.imap.port")}</label>
                      <input value={imapPort} onChange={(e) => setImapPort(e.target.value)} type="number" min={1} max={65535} disabled={!canEdit} />
                    </div>
                  </div>
                  <div className="form-grid form-grid-2">
                    <div className="field">
                      <label>{t("settings.imap.folder")}</label>
                      <input value={imapFolder} onChange={(e) => setImapFolder(e.target.value)} placeholder="INBOX" disabled={!canEdit} />
                    </div>
                    <div className="field">
                      <label>{t("settings.imap.pollSeconds")}</label>
                      <input value={imapPollSeconds} onChange={(e) => setImapPollSeconds(e.target.value)} type="number" min={15} max={3600} disabled={!canEdit} />
                      <small style={{ color: "var(--text-muted)" }}>{t("settings.imap.pollSecondsHelp")}</small>
                    </div>
                  </div>
                  <div className="field">
                    <label className="inline-checkbox">
                      <input type="checkbox" checked={imapUseTls} onChange={(e) => setImapUseTls(e.target.checked)} disabled={!canEdit} />
                      {t("settings.imap.tls")}
                    </label>
                  </div>
                </fieldset>

                <fieldset className="settings-fieldset">
                  <legend>{t("settings.section.imapAuth")}</legend>
                  <div className="form-grid form-grid-2">
                    <div className="field">
                      <label>{t("settings.imap.user")}</label>
                      <input value={imapUser} onChange={(e) => setImapUser(e.target.value)} disabled={!canEdit} />
                    </div>
                    <div className="field">
                      <label>{t("settings.imap.pass")}</label>
                      <input
                        value={imapPass}
                        onChange={(e) => setImapPass(e.target.value)}
                        type="password"
                        placeholder={current.imapPassSet ? SECRET_PLACEHOLDER : t("settings.outbound.smtpPassPlaceholder")}
                        disabled={!canEdit}
                      />
                      <small style={{ color: "var(--text-muted)" }}>
                        {current.imapPassSet ? t("settings.imap.passSet") : t("settings.imap.passUnset")}
                      </small>
                    </div>
                  </div>
                  {canEdit && current.imapHost && (
                    <div className="field" style={{ marginBottom: 0 }}>
                      <button
                        type="button"
                        onClick={async () => {
                          setImapTesting(true);
                          setImapResult(null);
                          try {
                            const res = await api.post<{ ok: boolean; processed: number; lastUid: number; durationMs: number; error?: string }>(
                              "/api/email/imap-poll",
                            );
                            setImapResult(res);
                          } catch (err) {
                            setImapResult({ ok: false, processed: 0, lastUid: 0, durationMs: 0, error: (err as Error).message });
                          } finally {
                            setImapTesting(false);
                          }
                        }}
                        disabled={imapTesting}
                      >
                        {imapTesting ? t("settings.imap.testing") : t("settings.imap.test")}
                      </button>
                      {imapResult && (
                        <div style={{ marginTop: 8, color: imapResult.ok ? "var(--ok)" : "var(--danger)", fontSize: 12 }}>
                          {imapResult.ok
                            ? t("settings.imap.testOk", { processed: imapResult.processed, lastUid: imapResult.lastUid, ms: imapResult.durationMs })
                            : t("settings.imap.testFailed", { error: imapResult.error ?? "—" })}
                        </div>
                      )}
                      <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
                        {t("settings.imap.testHint")}
                      </small>
                    </div>
                  )}
                </fieldset>
              </>
            )}
          </div>
        )}

        {tab === "webhooks" && (
          <>
            {(inboundMethod === "webhook" || inboundMethod === "both") && (
              <div className="card">
                <h3 style={{ marginTop: 0 }}>{t("settings.section.inboundWebhookHeading")}</h3>
                <p style={{ color: "var(--text-muted)", marginTop: 0, fontSize: 13 }}>
                  {t("settings.section.inboundWebhookHelp")}
                </p>
                <div className="field">
                  <label>{t("settings.inbound.webhookUrl")}</label>
                  <div className="copy-row">
                    <code className="url-block">{inboundWebhookUrl(current.orgId)}</code>
                    <CopyButton text={inboundWebhookUrl(current.orgId)} />
                  </div>
                  <small style={{ color: "var(--text-muted)" }}>
                    {t("settings.inbound.webhookUrlOrgHint", { orgId: current.orgId })}
                  </small>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <button type="button" onClick={() => setShowPayload((s) => !s)}>
                    {showPayload ? t("settings.payloadHide") : t("settings.payloadShow")}
                  </button>
                  {showPayload && (
                    <div style={{ marginTop: 10 }}>
                      <small style={{ color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
                        {t("settings.inbound.payloadHelp")}
                      </small>
                      <div className="copy-row">
                        <pre className="payload-block">{INBOUND_PAYLOAD_EXAMPLE}</pre>
                        <CopyButton text={INBOUND_PAYLOAD_EXAMPLE} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="card">
              <h3 style={{ marginTop: 0 }}>{t("settings.outbound.resendWebhookHeading")}</h3>
              <p style={{ color: "var(--text-muted)", marginTop: 0, fontSize: 13 }}>
                {t("settings.outbound.resendWebhookHelp")}
                {outboundProvider !== "resend" && <> {t("settings.outbound.resendOnly")}</>}
              </p>

              <div className="field">
                <label>{t("settings.outbound.resendWebhookUrl")}</label>
                <div className="copy-row">
                  <code className="url-block">{resendWebhookUrl(current.orgId)}</code>
                  <CopyButton text={resendWebhookUrl(current.orgId)} />
                </div>
                <small style={{ color: "var(--text-muted)" }}>
                  {t("settings.outbound.resendWebhookUrlOrgHint", { orgId: current.orgId })}
                </small>
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label>{t("settings.outbound.resendWebhookSecret")}</label>
                <input
                  value={resendWebhookSecret}
                  onChange={(e) => setResendWebhookSecret(e.target.value)}
                  type="password"
                  placeholder={current.resendWebhookSecretSet ? SECRET_PLACEHOLDER : "whsec_xxxxxxxx"}
                  disabled={!canEdit}
                  minLength={8}
                />
                <small style={{ color: "var(--text-muted)" }}>
                  {current.resendWebhookSecretSet
                    ? t("settings.outbound.resendWebhookSecretSet")
                    : t("settings.outbound.resendWebhookSecretUnset")}
                </small>
              </div>
            </div>
          </>
        )}

        {tab === "templates" && (
          <EmailTemplatesPanel canEdit={canEdit} onError={(err) => setMessage({ kind: "error", text: err })} />
        )}

        {message && (
          <div className="card" style={{ color: message.kind === "ok" ? "var(--ok)" : "var(--danger)" }}>
            {message.text}
          </div>
        )}

        {canEdit && tab !== "templates" && (
          <div className="settings-save-bar">
            <button className="primary" type="submit" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        )}
      </form>
    </>
  );
}

// ---------- Email templates panel ----------

type TemplateKind = "confirmation" | "agent_reply" | "csat" | "reopen_ack";
const TEMPLATE_KINDS: TemplateKind[] = ["confirmation", "agent_reply", "csat", "reopen_ack"];

interface ResolvedTemplate {
  lang: string;
  kind: TemplateKind;
  subject: string;
  body: string;
  source: "override" | "builtin" | "fallback";
}

interface TemplatesResponse {
  defaultLang: string;
  builtInLangs: string[];
  kinds: TemplateKind[];
  overrides: Array<{ lang: string; kind: TemplateKind; subject: string; body: string; updated_at: string }>;
  resolved: ResolvedTemplate[];
}

const COMMON_LANGS = ["en", "nl", "fr", "de", "es", "pt", "it", "pl", "ru", "zh", "ja", "ko", "ar", "tr", "uk", "sv", "no", "da", "fi", "cs", "el", "ro", "hu", "he", "th", "vi", "id"];
const LANG_CODE_RE = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/i;

function langDisplayName(code: string): string {
  try {
    const name = new Intl.DisplayNames([navigator.language || "en"], { type: "language" }).of(code);
    if (name && name.toLowerCase() !== code.toLowerCase()) return name;
  } catch { /* older runtime */ }
  return code;
}

function EmailTemplatesPanel({ canEdit, onError }: { canEdit: boolean; onError: (msg: string) => void }) {
  const api = useApi();
  const t = useT();
  const [data, setData] = useState<TemplatesResponse | null>(null);
  const [activeLang, setActiveLang] = useState<string>("en");
  const [activeKind, setActiveKind] = useState<TemplateKind>("confirmation");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addLang, setAddLang] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [defaultLangBusy, setDefaultLangBusy] = useState(false);
  const [okFlash, setOkFlash] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);

  // When the operator clicks "Show preview", the panel renders below
  // the editor — and on tall screens it can sit just out of view. The
  // effect waits one paint so the panel exists in the DOM, then
  // scrolls it into view.
  useEffect(() => {
    if (!previewOpen) return;
    const id = requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [previewOpen]);

  const load = async () => {
    const res = await api.get<TemplatesResponse>("/api/email-templates");
    setData(res);
  };

  useEffect(() => {
    load().catch((err) => onError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the active (lang, kind) selection changes, populate the
  // textareas from the resolved template so the editor always shows
  // *something* — built-in defaults included.
  useEffect(() => {
    if (!data) return;
    const tpl = data.resolved.find((r) => r.lang === activeLang && r.kind === activeKind);
    if (!tpl) return;
    setDraftSubject(tpl.subject);
    setDraftBody(tpl.body);
  }, [data, activeLang, activeKind]);

  // Client-side render of the active draft so the preview updates
  // live as the operator types — no server round-trip, no failure
  // modes, always in sync with the textarea content. The placeholder
  // names mirror what renderTemplate() does on the backend.
  //
  // These hooks live ABOVE the early-return below — moving them
  // under it broke the rules of hooks (different hook counts across
  // renders) and threw React error #310 when data finally arrived.
  const previewSampleSubject = "Cannot log in";
  const previewSampleBody = "Hi, here's the answer to your question…";
  const previewSampleTicket = 1042;
  const previewSampleCsatLink = "https://example.com/csat/abcd-1234";
  const previewSubject = useMemo(() => {
    const params: Record<string, string | number> = {
      ticketNumber: previewSampleTicket,
      subject: previewSampleSubject,
      body: previewSampleBody,
      csatLink: previewSampleCsatLink,
    };
    return draftSubject.replace(/\{(\w+)\}/g, (_, k: string) => (
      params[k] != null ? String(params[k]) : `{${k}}`
    ));
  }, [draftSubject]);
  const previewBody = useMemo(() => {
    const params: Record<string, string | number> = {
      ticketNumber: previewSampleTicket,
      subject: previewSampleSubject,
      body: previewSampleBody,
      csatLink: previewSampleCsatLink,
    };
    return draftBody.replace(/\{(\w+)\}/g, (_, k: string) => (
      params[k] != null ? String(params[k]) : `{${k}}`
    ));
  }, [draftBody]);

  if (!data) return <div className="card"><p>{t("common.loading")}</p></div>;

  // Languages shown as tabs: built-ins always present so the editor
  // can be used out of the box, plus any custom language the admin
  // already added overrides for.
  const allLangs = Array.from(new Set<string>([...data.builtInLangs, ...data.overrides.map((o) => o.lang)]));
  // Move the org default to the front so it's the first thing the
  // admin sees when opening the tab.
  allLangs.sort((a, b) => {
    if (a === data.defaultLang) return -1;
    if (b === data.defaultLang) return 1;
    return a.localeCompare(b);
  });

  const activeResolved = data.resolved.find((r) => r.lang === activeLang && r.kind === activeKind);
  const isOverride = activeResolved?.source === "override";

  const flashOk = (msg: string) => {
    setOkFlash(msg);
    setTimeout(() => setOkFlash(null), 2400);
  };

  const saveTemplate = async () => {
    if (!canEdit || !draftSubject.trim() || !draftBody.trim()) return;
    setSavingTpl(true);
    try {
      await api.put(`/api/email-templates/${encodeURIComponent(activeLang)}/${encodeURIComponent(activeKind)}`, {
        subject: draftSubject,
        body: draftBody,
      });
      await load();
      flashOk(t("settings.templates.savedFlash"));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setSavingTpl(false);
    }
  };

  const resetTemplate = async () => {
    if (!canEdit) return;
    const ok = await confirmDialog({
      message: t("settings.templates.resetConfirm"),
      confirmLabel: t("settings.templates.resetToDefault"),
      variant: "danger",
    });
    if (!ok) return;
    setSavingTpl(true);
    try {
      await api.del(`/api/email-templates/${encodeURIComponent(activeLang)}/${encodeURIComponent(activeKind)}`);
      await load();
      flashOk(t("settings.templates.resetFlash"));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setSavingTpl(false);
    }
  };

  const setDefaultLang = async (lang: string) => {
    if (!canEdit) return;
    setDefaultLangBusy(true);
    try {
      await api.patch("/api/email-templates/default-lang", { defaultLang: lang });
      await load();
      flashOk(t("settings.templates.defaultUpdated"));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setDefaultLangBusy(false);
    }
  };

  const addLanguage = async () => {
    setAddError(null);
    const lang = addLang.trim().toLowerCase();
    if (!LANG_CODE_RE.test(lang)) {
      setAddError(t("settings.templates.langCodeInvalid"));
      return;
    }
    if (allLangs.includes(lang)) {
      setAddError(t("settings.templates.langExists"));
      return;
    }
    setAddBusy(true);
    try {
      await api.post("/api/email-templates/add-language", { lang });
      setAddLang("");
      setAdding(false);
      await load();
      setActiveLang(lang);
      flashOk(t("settings.templates.langAdded"));
    } catch (err) {
      setAddError((err as Error).message);
    } finally {
      setAddBusy(false);
    }
  };

  const removeLanguage = async (lang: string) => {
    if (!canEdit) return;
    const ok = await confirmDialog({
      message: t("settings.templates.removeLangConfirm", { lang: langDisplayName(lang) }),
      confirmLabel: t("common.delete"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.del(`/api/email-templates/language/${encodeURIComponent(lang)}`);
      await load();
      // Switch to a still-existing tab if we just deleted the active
      // language and it was custom (non-built-in).
      if (!data.builtInLangs.includes(lang)) setActiveLang(data.defaultLang);
      flashOk(t("settings.templates.langRemoved"));
    } catch (err) {
      onError((err as Error).message);
    }
  };

  const kindLabel = (kind: TemplateKind): string => t(`settings.templates.kind.${kind}`);

  const sourceMessage = isOverride
    ? t("settings.templates.sourceOverride")
    : activeResolved?.source === "builtin"
    ? t("settings.templates.sourceBuiltin")
    : t("settings.templates.sourceFallback", { lang: activeResolved?.lang ?? "en" });

  return (
    <div className="card email-templates-panel">
      <div className="email-templates-header">
        <div>
          <h3 style={{ margin: 0 }}>{t("settings.templates.heading")}</h3>
          <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: 13 }}>
            {t("settings.templates.intro")}
          </p>
        </div>
        <div className="email-templates-default-lang">
          <label htmlFor="email-templates-default-lang">
            {t("settings.templates.defaultLang")}
          </label>
          <select
            id="email-templates-default-lang"
            value={data.defaultLang}
            onChange={(e) => setDefaultLang(e.target.value)}
            disabled={!canEdit || defaultLangBusy}
          >
            {allLangs.map((l) => (
              <option key={l} value={l}>{langDisplayName(l)} ({l})</option>
            ))}
          </select>
          <small style={{ color: "var(--text-muted)", fontSize: 12 }}>
            {t("settings.templates.defaultLangHelp")}
          </small>
        </div>
      </div>

      <div className="email-templates-layout">
        {/* Left column: language picker. Vertical list on desktop, a
            horizontal scroller on phones to save vertical real-estate. */}
        <aside className="email-templates-langs" aria-label={t("settings.templates.languages")}>
          <div className="email-templates-langs-heading">{t("settings.templates.languages")}</div>
          <ul className="email-templates-lang-list">
            {allLangs.map((l) => {
              const removable = !data.builtInLangs.includes(l);
              const active = l === activeLang;
              const isDefault = l === data.defaultLang;
              return (
                <li
                  key={l}
                  className={`email-templates-lang-item${active ? " active" : ""}`}
                >
                  <button
                    type="button"
                    className="email-templates-lang-button"
                    onClick={() => setActiveLang(l)}
                    aria-pressed={active}
                  >
                    <span className="email-templates-lang-name">{langDisplayName(l)}</span>
                    <span className="email-templates-lang-code">{l}</span>
                    {isDefault && (
                      <span
                        className="email-templates-lang-default"
                        title={t("settings.templates.defaultLang")}
                        aria-label={t("settings.templates.defaultLang")}
                      >
                        ★
                      </span>
                    )}
                  </button>
                  {removable && canEdit && (
                    <button
                      type="button"
                      className="email-templates-lang-remove"
                      aria-label={t("settings.templates.removeLangAria", { lang: langDisplayName(l) })}
                      title={t("settings.templates.removeLangAria", { lang: langDisplayName(l) })}
                      onClick={() => removeLanguage(l)}
                    >
                      ✕
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {canEdit && !adding && (
            <button
              type="button"
              className="email-templates-add-lang-trigger"
              onClick={() => setAdding(true)}
            >
              + {t("settings.templates.addLanguage")}
            </button>
          )}
          {adding && (
            <div className="email-templates-add-lang-panel">
              <div className="field" style={{ marginBottom: 6 }}>
                <label>{t("settings.templates.addLanguagePrompt")}</label>
                <input
                  value={addLang}
                  onChange={(e) => setAddLang(e.target.value)}
                  placeholder="e.g. it, es, zh-CN"
                  list="settings-templates-langs"
                  autoFocus
                />
                <datalist id="settings-templates-langs">
                  {COMMON_LANGS.filter((c) => !allLangs.includes(c)).map((c) => (
                    <option key={c} value={c}>{langDisplayName(c)}</option>
                  ))}
                </datalist>
                <small style={{ color: "var(--text-muted)", fontSize: 12 }}>
                  {t("settings.templates.addLanguageHelp")}
                </small>
              </div>
              {addError && (
                <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 6 }}>{addError}</div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="primary"
                  onClick={addLanguage}
                  disabled={addBusy || !addLang.trim()}
                >
                  {addBusy ? t("settings.templates.translating") : t("settings.templates.translateAndAdd")}
                </button>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setAddLang(""); setAddError(null); }}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Right column: kind selector + status banner + editor + actions. */}
        <div className="email-templates-editor">
          <div className="email-templates-kind-tabs" role="tablist" aria-label={t("settings.templates.kind.label")}>
            {TEMPLATE_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={k === activeKind}
                className={`email-templates-kind-tab${k === activeKind ? " active" : ""}`}
                onClick={() => setActiveKind(k)}
              >
                {kindLabel(k)}
              </button>
            ))}
          </div>

          <div
            className={`email-templates-status email-templates-status-${activeResolved?.source ?? "builtin"}`}
            role="status"
          >
            <span className="email-templates-status-dot" aria-hidden="true" />
            <span>{sourceMessage}</span>
          </div>

          <div className="field">
            <label>{t("settings.templates.subject")}</label>
            <input
              value={draftSubject}
              onChange={(e) => setDraftSubject(e.target.value)}
              disabled={!canEdit}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("settings.templates.subjectHelp")}</small>
          </div>

          <div className="field">
            <label>{t("settings.templates.body")}</label>
            <textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={14}
              disabled={!canEdit}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("settings.templates.placeholdersHelp")}</small>
          </div>

          <div className="email-templates-actions">
            <div className="email-templates-actions-primary">
              {canEdit && (
                <button
                  type="button"
                  className="primary"
                  onClick={saveTemplate}
                  disabled={savingTpl || !draftSubject.trim() || !draftBody.trim()}
                >
                  {savingTpl ? t("common.saving") : t("common.save")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setPreviewOpen((v) => !v)}
                aria-pressed={previewOpen}
                aria-expanded={previewOpen}
              >
                {previewOpen ? t("settings.templates.previewHide") : t("settings.templates.previewShow")}
              </button>
            </div>
            {canEdit && isOverride && (
              <button
                type="button"
                className="email-templates-reset"
                onClick={resetTemplate}
                disabled={savingTpl}
              >
                {t("settings.templates.resetToDefault")}
              </button>
            )}
          </div>

          {okFlash && (
            <div className="email-templates-flash">{okFlash}</div>
          )}

          {previewOpen && (
            <div className="email-templates-preview" ref={previewRef}>
              <div className="email-templates-preview-heading">
                {t("settings.templates.previewHeading")}
              </div>
              <div className="email-templates-preview-meta">
                {t("settings.templates.previewMeta", { n: String(previewSampleTicket) })}
              </div>
              <div className="email-templates-preview-subject">
                [#{previewSampleTicket}] {previewSubject || <span style={{ color: "var(--text-muted)" }}>—</span>}
              </div>
              <pre className="email-templates-preview-body">
                {previewBody || (
                  <span style={{ color: "var(--text-muted)" }}>
                    {t("settings.templates.previewEmpty")}
                  </span>
                )}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryItem({ label, value, status }: { label: string; value: string; status: Status }) {
  const t = useT();
  const statusLabel = status === "ready"
    ? t("settings.summary.statusReady")
    : status === "incomplete"
    ? t("settings.summary.statusIncomplete")
    : t("settings.summary.statusEmpty");
  return (
    <div className="settings-summary-item">
      <div className="settings-summary-label">{label}</div>
      <div className="settings-summary-value">{value}</div>
      <div className={`settings-summary-status status-${status}`}>
        <span className="settings-summary-dot" aria-hidden="true" />
        {statusLabel}
      </div>
    </div>
  );
}
